import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    TextField,
    Slider,
    Tooltip,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import { useHelpContent } from '../../utils/HelpUtils';

interface ExpandTreeDialogProps {
    open: boolean;
    onClose: () => void;
    onProceed: (context: string, maxDepth: number) => Promise<void>;
    fileNames: string[];
    currentDocumentName: string;
}

const ExpandTreeDialog: React.FC<ExpandTreeDialogProps> = ({
    open,
    onClose,
    onProceed,
    fileNames,
    currentDocumentName,
}) => {
    const [context, setContext] = useState('');
    const [maxDepth, setMaxDepth] = useState(1);
    const [loading, setLoading] = useState(false);
    const { getTooltipTextForState } = useHelpContent();

    const handleProceed = async () => {
        setLoading(true);
        try {
            await onProceed(context.trim(), maxDepth);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setContext('');
        setMaxDepth(1);
        onClose();
    };

    const isProceedDisabled = () => {
        return loading;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Expand Existing Document with New Content</DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    {loading && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: 'rgba(255,255,255,0.9)',
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        </Box>
                    )}
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Current Document: <strong>{currentDocumentName}</strong>
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                            New Files to Process:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {fileNames.map((fileName, index) => (
                                <Chip key={index} label={fileName} variant="outlined" size="small" />
                            ))}
                        </Box>
                    </Box>

                </Box>
                
                <Typography variant="body1" gutterBottom>
                    <strong>Optional Context:</strong> Provide additional context to help the AI better integrate new content with your existing category structure.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Examples of helpful context:
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "This is a continuation of the previous content"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "These files contain related but different topics"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "Organize by chronological order"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • "Focus on technical implementation details"
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Context (Optional)"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Describe how this new content relates to your existing structure..."
                    sx={{ mb: 3 }}
                />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Maximum Tree Depth: {maxDepth}
                    </Typography>
                    
                    <Tooltip title="Set the maximum number of levels deep new categories can be created">
                        <Slider
                            value={maxDepth}
                            onChange={(_, value) => setMaxDepth(value as number)}
                            min={1}
                            max={5}
                            marks
                            valueLabelDisplay="auto"
                            sx={{ mt: 1 }}
                        />
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                        Controls how deep new category hierarchies can be created
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        The AI will analyze your existing category structure and:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        • Place new content segments in existing categories when there's a good match
                    </Typography>
                    <Typography variant="body2" component="div">
                        • Suggest new categories for content that doesn't fit existing structure
                    </Typography>
                    <Typography variant="body2" component="div">
                        • Maintain your current organization while expanding it intelligently
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={isProceedDisabled()}
                    startIcon={<AddIcon />}
                >
                    Expand Document
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpandTreeDialog; 